const createTextSegment = (textKey, options = {}) => ({
  textKey,
  strong: false,
  danger: false,
  ...options,
})

const createTextItem = (textKey) => ({ textKey })

const createSegmentItem = (segments) => ({ segments })

const createTitleBlock = (textKey) => ({
  type: "title",
  textKey,
})

const createSubtitleBlock = (textKey) => ({
  type: "subtitle",
  textKey,
})

const createParagraphBlock = (textKey) => ({
  type: "paragraph",
  textKey,
})

const createRichTextBlock = (segments) => ({
  type: "richText",
  segments,
})

const createListBlock = (items, titleKey = "") => ({
  type: "list",
  titleKey,
  items,
})

const normalizeSegments = (segments) => segments.map((segment) => createTextSegment(
  segment.textKey,
  { strong: segment.strong, danger: segment.danger },
))

const normalizeListItems = (items) => items.map((item) => {
  if (item.textKey) {
    return createTextItem(item.textKey)
  }

  return createSegmentItem(normalizeSegments(item.segments))
})

const normalizeBlocks = (scopeId, blocks) => Object.freeze(
  blocks.map((block, index) => {
    const normalizedBlock = {
      ...block,
      id: `${scopeId}-${block.type}-${index + 1}`,
    }

    if (block.type === "richText") {
      normalizedBlock.segments = normalizeSegments(block.segments)
    }

    if (block.type === "list") {
      normalizedBlock.items = normalizeListItems(block.items)
    }

    return Object.freeze(normalizedBlock)
  }),
)

const resolveExpanded = (section) => {
  if (typeof section.isExpanded === "boolean") {
    return section.isExpanded
  }

  if (typeof section.defaultExpanded === "boolean") {
    return section.defaultExpanded
  }

  return true
}

const createChildSection = ({ id, titleKey, isExpanded, defaultExpanded, blocks = [] }) => Object.freeze({
  id,
  titleKey,
  isExpanded: resolveExpanded({ isExpanded, defaultExpanded }),
  blocks: normalizeBlocks(id, blocks),
})

const createSection = ({
  id,
  titleKey,
  isExpanded,
  defaultExpanded,
  blocks = [],
  childSections = [],
}) => Object.freeze({
  id,
  titleKey,
  isExpanded: resolveExpanded({ isExpanded, defaultExpanded }),
  blocks: normalizeBlocks(id, blocks),
  childSections: Object.freeze(childSections.map((section) => createChildSection(section))),
})

const flattenSectionIds = (sections) => sections.flatMap((section) => [
  [section.id, section.isExpanded !== false],
  ...section.childSections.map((childSection) => [childSection.id, childSection.isExpanded !== false]),
])

export const whatsappDocIntroBlocks = normalizeBlocks("intro", [
  createTitleBlock("whatsapp.doc.h1"),
  createParagraphBlock("whatsapp.doc.desc"),
])

export const whatsappDocSections = Object.freeze([
  createSection({
    id: "part1",
    titleKey: "whatsapp.doc.section1.title",
    blocks: [
      createRichTextBlock([
        createTextSegment("whatsapp.doc.section1.p1.before"),
        createTextSegment("whatsapp.doc.section1.p1.bold", { strong: true }),
        createTextSegment("whatsapp.doc.section1.p1.after"),
      ]),
      createListBlock([
        createTextItem("whatsapp.doc.section1.list1.item1"),
        createTextItem("whatsapp.doc.section1.list1.item2"),
        createTextItem("whatsapp.doc.section1.list1.item3"),
      ]),
      createRichTextBlock([
        createTextSegment("whatsapp.doc.section1.note.label", { strong: true }),
        createTextSegment("whatsapp.doc.section1.note.text"),
      ]),
    ],
  }),
  createSection({
    id: "part2",
    titleKey: "whatsapp.doc.section2.title",
    childSections: [
      {
        id: "part_child_2_1",
        titleKey: "whatsapp.doc.section2.sub1.title",
        blocks: [
          createListBlock([
            createTextItem("whatsapp.doc.section2.sub1.item1"),
            createTextItem("whatsapp.doc.section2.sub1.item2"),
          ]),
        ],
      },
      {
        id: "part_child_2_2",
        titleKey: "whatsapp.doc.section2.sub2.title",
        blocks: [
          createListBlock([
            createTextItem("whatsapp.doc.section2.sub2.item1"),
            createTextItem("whatsapp.doc.section2.sub2.item2"),
          ]),
        ],
      },
      {
        id: "part_child_2_3",
        titleKey: "whatsapp.doc.section2.sub3.title",
        blocks: [
          createListBlock([
            createTextItem("whatsapp.doc.section2.sub3.item1"),
            createTextItem("whatsapp.doc.section2.sub3.item2"),
          ]),
        ],
      },
      {
        id: "part_child_2_4",
        titleKey: "whatsapp.doc.section2.sub4.title",
        blocks: [
          createListBlock([
            createTextItem("whatsapp.doc.section2.sub4.item1"),
            createTextItem("whatsapp.doc.section2.sub4.item2"),
            createSegmentItem([
              createTextSegment("whatsapp.doc.section2.sub4.item3.bold", { strong: true }),
              createTextSegment("whatsapp.doc.section2.sub4.item3.after"),
            ]),
          ]),
        ],
      },
      {
        id: "part_child_2_5",
        titleKey: "whatsapp.doc.section2.sub5.title",
        blocks: [
          createListBlock([
            createSegmentItem([
              createTextSegment("whatsapp.doc.section2.sub5.item1.before"),
              createTextSegment("whatsapp.doc.section2.sub5.item1.bold", { strong: true }),
              createTextSegment("whatsapp.doc.section2.sub5.item1.after"),
            ]),
            createTextItem("whatsapp.doc.section2.sub5.item2"),
            createTextItem("whatsapp.doc.section2.sub5.item3"),
          ]),
        ],
      },
    ],
  }),
  createSection({
    id: "part3",
    titleKey: "whatsapp.doc.section3.title",
    blocks: [
      createListBlock([
        createSegmentItem([
          createTextSegment("whatsapp.doc.section3.item1.bold", { strong: true }),
          createTextSegment("whatsapp.doc.section3.item1.after"),
        ]),
        createSegmentItem([
          createTextSegment("whatsapp.doc.section3.item2.bold", { strong: true }),
          createTextSegment("whatsapp.doc.section3.item2.after"),
        ]),
        createSegmentItem([
          createTextSegment("whatsapp.doc.section3.item3.bold", { strong: true }),
          createTextSegment("whatsapp.doc.section3.item3.after"),
        ]),
      ]),
    ],
  }),
  createSection({
    id: "part4",
    titleKey: "whatsapp.doc.section4.title",
    childSections: [
      {
        id: "part_child_4_1",
        titleKey: "whatsapp.doc.section4.sub1.title",
        blocks: [
          createListBlock([
            createTextItem("whatsapp.doc.section4.sub1.item1"),
            createTextItem("whatsapp.doc.section4.sub1.item2"),
            createTextItem("whatsapp.doc.section4.sub1.item3"),
          ]),
        ],
      },
      {
        id: "part_child_4_2",
        titleKey: "whatsapp.doc.section4.sub2.title",
        blocks: [
          createListBlock([
            createTextItem("whatsapp.doc.section4.sub2.item1"),
            createTextItem("whatsapp.doc.section4.sub2.item2"),
            createTextItem("whatsapp.doc.section4.sub2.item3"),
          ]),
        ],
      },
      {
        id: "part_child_4_3",
        titleKey: "whatsapp.doc.section4.sub3.title",
        blocks: [
          createRichTextBlock([
            createTextSegment("whatsapp.doc.section4.sub3.p1.before"),
            createTextSegment("whatsapp.doc.section4.sub3.p1.bold", { strong: true }),
            createTextSegment("whatsapp.doc.section4.sub3.p1.after"),
          ]),
          createSubtitleBlock("whatsapp.doc.section4.sub3.week1.title"),
          createListBlock([
            createTextItem("whatsapp.doc.section4.sub3.week1.item1"),
            createTextItem("whatsapp.doc.section4.sub3.week1.item2"),
            createTextItem("whatsapp.doc.section4.sub3.week1.item3"),
          ]),
          createSubtitleBlock("whatsapp.doc.section4.sub3.week2.title"),
          createListBlock([
            createTextItem("whatsapp.doc.section4.sub3.week2.item1"),
            createTextItem("whatsapp.doc.section4.sub3.week2.item2"),
          ]),
          createSubtitleBlock("whatsapp.doc.section4.sub3.week3.title"),
          createListBlock([
            createTextItem("whatsapp.doc.section4.sub3.week3.item1"),
            createTextItem("whatsapp.doc.section4.sub3.week3.item2"),
          ]),
        ],
      },
      {
        id: "part_child_4_4",
        titleKey: "whatsapp.doc.section4.sub4.title",
        blocks: [
          createListBlock([
            createTextItem("whatsapp.doc.section4.sub4.item1"),
            createTextItem("whatsapp.doc.section4.sub4.item2"),
            createTextItem("whatsapp.doc.section4.sub4.item3"),
            createTextItem("whatsapp.doc.section4.sub4.item4"),
          ]),
          createRichTextBlock([
            createTextSegment("whatsapp.doc.section4.sub4.note.label", { strong: true }),
            createTextSegment("whatsapp.doc.section4.sub4.note.text"),
          ]),
        ],
      },
      {
        id: "part_child_4_5",
        titleKey: "whatsapp.doc.section4.sub5.title",
        blocks: [
          createListBlock([
            createTextItem("whatsapp.doc.section4.sub5.item1"),
            createTextItem("whatsapp.doc.section4.sub5.item2"),
            createTextItem("whatsapp.doc.section4.sub5.item3"),
            createTextItem("whatsapp.doc.section4.sub5.item4"),
            createTextItem("whatsapp.doc.section4.sub5.item5"),
          ]),
        ],
      },
      {
        id: "part_child_4_6",
        titleKey: "whatsapp.doc.section4.sub6.title",
        blocks: [
          createParagraphBlock("whatsapp.doc.section4.sub6.p1"),
          createListBlock([
            createTextItem("whatsapp.doc.section4.sub6.item1"),
            createTextItem("whatsapp.doc.section4.sub6.item2"),
            createTextItem("whatsapp.doc.section4.sub6.item3"),
          ]),
          createRichTextBlock([
            createTextSegment("whatsapp.doc.section4.sub6.note.label", { strong: true }),
            createTextSegment("whatsapp.doc.section4.sub6.note.text"),
          ]),
        ],
      },
    ],
  }),
  createSection({
    id: "part6",
    titleKey: "whatsapp.doc.section6.title",
    blocks: [
      createParagraphBlock("whatsapp.doc.section6.p1"),
    ],
    childSections: [
      {
        id: "part_child_6_1",
        titleKey: "whatsapp.doc.section6.sub1.title",
        blocks: [
          createParagraphBlock("whatsapp.doc.section6.sub1.p1"),
        ],
      },
      {
        id: "part_child_6_2",
        titleKey: "whatsapp.doc.section6.sub2.title",
        blocks: [
          createListBlock([
            createTextItem("whatsapp.doc.section6.sub2.item1"),
            createTextItem("whatsapp.doc.section6.sub2.item2"),
            createTextItem("whatsapp.doc.section6.sub2.item3"),
          ]),
        ],
      },
      {
        id: "part_child_6_3",
        titleKey: "whatsapp.doc.section6.sub3.title",
        blocks: [
          createParagraphBlock("whatsapp.doc.section6.sub3.p1"),
          createListBlock([
            createTextItem("whatsapp.doc.section6.sub3.item1"),
            createTextItem("whatsapp.doc.section6.sub3.item2"),
            createTextItem("whatsapp.doc.section6.sub3.item3"),
          ]),
          createRichTextBlock([
            createTextSegment("whatsapp.doc.section6.sub3.note.label", { strong: true }),
            createTextSegment("whatsapp.doc.section6.sub3.note.text"),
          ]),
        ],
      },
    ],
  }),
  createSection({
    id: "part7",
    titleKey: "whatsapp.doc.section7.title",
    blocks: [
      createParagraphBlock("whatsapp.doc.section7.p1"),
      createListBlock([
        createTextItem("whatsapp.doc.section7.item1"),
        createTextItem("whatsapp.doc.section7.item2"),
        createTextItem("whatsapp.doc.section7.item3"),
      ]),
    ],
  }),
  createSection({
    id: "part8",
    titleKey: "whatsapp.doc.section8.title",
    blocks: [
      createParagraphBlock("whatsapp.doc.section8.p1"),
      createParagraphBlock("whatsapp.doc.section8.p2"),
    ],
  }),
])

export const createWhatsappDocCollapseState = () => Object.fromEntries(
  flattenSectionIds(whatsappDocSections),
)

export const createWhatsappDocTitleList = (t, collapseState) => whatsappDocSections.map((section) => ({
  id: section.id,
  title: t(section.titleKey),
  href: `#${section.id}`,
  isExpanded: collapseState[section.id],
  childList: section.childSections.map((childSection) => ({
    title: t(childSection.titleKey),
    href: `#${childSection.id}`,
  })),
}))
